import React from 'react'
import { Link } from 'react-router-dom'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  SEARCH_TYPES,
  DATA_TYPES,
  LOOKUP_TYPES,
  Icon,
  reducers,
  coreConstants,
} from '@adc/ui-morpheus'
import { VENUE_TEMPLATES } from '../../constants/tableTypes'
import { venueType as venueTypeList } from '../../constants/optionLists'

const { states: statesList, yesNo: yesNoList } = coreConstants.optionLists
const defaultColPropsWithWidth300 = {
  width: 300,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}
const defaultColPropsWithWidth100 = {
  width: 100,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}

export const tableConfig = {
  config: {
    entityKey: VENUE_TEMPLATES,
    primaryKeyField: 'venue_template_id',
    allowExportCSV: true,
  },
  fieldDefinitions: [
    {
      name: 'Selected',
      apiRef: 'selected', // not api val
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.CHECKBOX,
        colProps: { width: 40, fixed: true },
      },
    },
    {
      name: 'Template Name',
      apiRef: 'template_name',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.SORTED,
          sortAscending: true,
          searchable: true,
        },
      },
    },
    {
      name: 'Name',
      apiRef: 'venue_name',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Title',
      apiRef: 'venue_title',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: { ...defaultColPropsWithWidth300, isVisible: true },
      },
    },
    {
      name: 'Type',
      apiRef: 'venue_configuration.venue_type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: venueTypeList,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Trustee',
      apiRef: 'trustee',
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Auction.com Auctioneer',
      apiRef: 'venue_configuration.auctioncom_auctioneer',
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'URL',
      apiRef: 'venue_url',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Address',
      apiRef: 'address',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'City',
      apiRef: 'venue_address.municipality',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'State',
      apiRef: 'venue_address.country_primary_subdivision',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: statesList,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Zip',
      apiRef: 'venue_address.postal_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'County',
      apiRef: 'venue_address.country_secondary_subdivision',
      searchType: SEARCH_TYPES.SUGGEST_ASYNC,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Created Date',
      apiRef: 'created_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Created By',
      apiRef: 'created_by',
      foreignKey: LOOKUP_TYPES.USER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Updated Date',
      apiRef: 'update_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Updated By',
      apiRef: 'updated_by',
      foreignKey: LOOKUP_TYPES.USER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Status',
      apiRef: 'template_status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: [
        { value: 'ACTIVE', text: 'Active' },
        { value: 'INACTIVE', text: 'Inactive' },
      ],
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.STATUS,
        positiveValue: 'Active',
        negativeValue: 'Inactive',
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Holding Bucket',
      apiRef: 'venue_configuration.holding_venue',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Edit',
      apiRef: '__actions__', // not api val
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.ACTION,
        actions: ['Link'],
        colProps: { width: 50, fixedRight: true, align: 'center' },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'template_name.lowercase',
      order: 'asc',
    },
  ],
}

const linkColumnAction = () => (actionIndex, rowIndex, primaryId) =>
  (
    <Link
      style={{ margin: -8, padding: 8 }}
      key={actionIndex}
      to={`/schedule/venue-templates/${primaryId}/edit`}
      data-elm-id={`action_edit_${rowIndex}`}
    >
      <Icon
        size='large'
        style={{ marginTop: -3 }}
        color='info'
        name='write square'
      />
    </Link>
  )

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
export const columnSelector = state =>
  state.fieldDefinitions
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(linkColumnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      return column
    })

export const getFilterClauses = state => state.filterClauses

export default reducers.dataTable.dataTable(VENUE_TEMPLATES, tableConfig)
